import React, { useContext, useEffect, useState } from 'react';
import styles from './LandingPage.module.scss';
import makeSession from '../../utils/makeSession';
import getSession from '../../utils/getSession';
import ExternalLogin from '../../components/ExternalLogin/ExternalLogin';
import LandingPageContent from './LandingPageContent';
import LandingPageHeader from '../../components/LandingPageHeader/LandingPageHeader';
import { convertStringToDate } from '../../utils/date-conversions';
import bookfair from '../../interfaces/bookfair-info';
import { Pages } from '../../App';
import { updateLogInData, updateSessionData } from '../../DumbleData';
import { ConfigContext } from '../../providers/ConfigContext';
import SalesModal from '../SalesModal/SalesModal';
import BigNumber from 'bignumber.js';

interface Props {
	fairId: string | (string | null)[] | null;
	bookfairInfo: bookfair;
	setBookfairInfo: React.Dispatch<React.SetStateAction<bookfair>>;
	loggedIn: boolean;
	setLoggedIn: React.Dispatch<React.SetStateAction<boolean>>;
	setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
	modalDisplayed: boolean;
	setModalDisplayed: React.Dispatch<React.SetStateAction<boolean>>;
}

const [mySession] = makeSession();
const [acquireSession] = getSession();

const checkPaymentUnavailable = (info: any) => {
	const today = new Date();
	const fiveDaysAhead = convertStringToDate(info.endDate);
	fiveDaysAhead.setDate(fiveDaysAhead.getDate() + 5);
	fiveDaysAhead.setHours(23, 59, 59, 999);

	const fiveDaysBefore = new Date(info.startDate);
	fiveDaysBefore.setDate(fiveDaysBefore.getDate() - 5);
	fiveDaysBefore.setHours(0, 0, 0, 0);

	if (today > fiveDaysAhead || today < fiveDaysBefore) {
		return true;
	}
	return false;
};

const processSessionData = (
	data: any,
	setLoggedIn: React.Dispatch<React.SetStateAction<boolean>>,
	setBookfairInfo: React.Dispatch<React.SetStateAction<bookfair>>,
	setCurrentPage: React.Dispatch<React.SetStateAction<number>>,
	setClosedFairState: React.Dispatch<React.SetStateAction<boolean>>,
	setLoading: React.Dispatch<React.SetStateAction<boolean>>
) => {
	setLoggedIn(true);
	const info = {
		school: data.school.name,
		stfBalance: BigNumber(data.school.stfBalance),
		startDate: data.fair.deliveryDate,
		endDate: data.fair.pickupDate,
		id: data.fair.id,
		isFairEnded: data.isFairEnded,
		ccSales: data.sales.cc,
		ewalletSales: data.sales.ewallet,
		totalSales: data.sales.total,
		stfSales: data.sales.stf,
		taxStatus: data.school.taxStatus,
		taxRate: BigNumber(data.fair.taxRate),
		taxExemptSalesAllowed: data.fair.taxExemptSalesAllowed,
		maxTransactionItems: data.maxTransactionItems,
		ewalletEnabled: data.ewalletEnabled,
		chairpersonEmail: data.chairperson.email,
	};
	setBookfairInfo(info);

	if (checkPaymentUnavailable(info)) {
		setCurrentPage(Pages.NotAvailable);
		return;
	}
	if (info.isFairEnded) {
		setCurrentPage(Pages.SalesTotal);
		setClosedFairState(true);
	}
	setLoading(false);
};

// fill info with session info
const getSessionInfo = async (
	values: { fairId: string | (string | null)[] | null; apiBase: string },
	createSessionInfo: () => Promise<void>,
	setLoggedIn: React.Dispatch<React.SetStateAction<boolean>>,
	setBookfairInfo: React.Dispatch<React.SetStateAction<bookfair>>,
	setCurrentPage: React.Dispatch<React.SetStateAction<number>>,
	setClosedFairState: React.Dispatch<React.SetStateAction<boolean>>,
	setLoading: React.Dispatch<React.SetStateAction<boolean>>
) => {
	const parsedFairId = Array.isArray(values.fairId) ? parseFloat(values.fairId[0] || '0') : parseFloat(values.fairId || '0');
	if (parsedFairId !== 0) {
		createSessionInfo();
		return;
	}

	const { data, error } = await acquireSession(values.apiBase);

	if (error) {
		console.log(error);
		setLoggedIn(false);
		createSessionInfo();
		setLoading(false);
	} else if (data != null) {
		processSessionData(data, setLoggedIn, setBookfairInfo, setCurrentPage, setClosedFairState, setLoading);
	}
};

function LandingPage({ fairId, bookfairInfo, setBookfairInfo, loggedIn, setLoggedIn, setCurrentPage, modalDisplayed, setModalDisplayed }: Props) {
	const [closedFairState, setClosedFairState] = useState(false);
	const [loading, setLoading] = useState(true);
	const bookfairInfoPopulated = bookfairInfo.id !== 0;
	const { apiBase } = useContext(ConfigContext);

	// Calls the session endpoint and populates book fair data
	useEffect(() => {
		// request session with signed fields
		// creates cookie after we already signed in from another site
		const createSessionInfo = async () => {
			const { data, error } = await mySession(apiBase, fairId);
			if (error) {
				console.log(error);
				setLoggedIn(false);
				const errorMessage = error.errorMessage?.toLowerCase();
				const message = error.message?.toLowerCase();
				if (errorMessage?.includes('no valid fair') || message?.includes('fair ended without transactions') || error.status === 500) {
					setCurrentPage(Pages.NotAvailable);
				}
				setLoading(false);
			} else if (data != null) {
				processSessionData(data, setLoggedIn, setBookfairInfo, setCurrentPage, setClosedFairState, setLoading);
			}
		};

		getSessionInfo({ fairId, apiBase }, createSessionInfo, setLoggedIn, setBookfairInfo, setCurrentPage, setClosedFairState, setLoading);
	}, [fairId, setBookfairInfo, setCurrentPage, setLoggedIn, apiBase]);

	// Populate analytics data
	useEffect(() => {
		if (bookfairInfoPopulated) {
			updateLogInData();
			updateSessionData(bookfairInfo.school, bookfairInfo.startDate, bookfairInfo.endDate, bookfairInfo.id, bookfairInfo.taxStatus, bookfairInfo.taxRate.toNumber(), bookfairInfo.totalSales);
		}
	}, [bookfairInfo.endDate, bookfairInfo.id, bookfairInfo.school, bookfairInfo.startDate, bookfairInfo.taxRate, bookfairInfo.taxStatus, bookfairInfo.totalSales, bookfairInfoPopulated]);

	// Prompt sign in page or landing page
	const loadBfInfo = () => {
		// not logged in :(
		if (!loggedIn) {
			return (
				<div className={styles.LandingPage} data-testid="LandingPage">
					<ExternalLogin fairId={fairId} setLoggedIn={setLoggedIn} />
				</div>
			);
		}
		// means there is stuff (cookies)
		const convertedStartDate = convertStringToDate(bookfairInfo.startDate);
		const convertedEndDate = convertStringToDate(bookfairInfo.endDate);
		return (
			<>
				{bookfairInfoPopulated && (
					<>
						{!modalDisplayed && <SalesModal handleModal={setModalDisplayed} />}
						<LandingPageContent
							setCurrentPage={setCurrentPage}
							schoolName={bookfairInfo.school}
							startDate={convertedStartDate}
							endDate={convertedEndDate}
							fairId={bookfairInfo.id.toString()}
							taxStatus={bookfairInfo.taxStatus}
							taxRate={bookfairInfo.taxRate}
							totalSales={bookfairInfo.totalSales}
						/>
					</>
				)}
			</>
		);
	};

	const closedFairDesign = () => {
		if (!loggedIn) {
			return (
				<div className={styles.LandingPage} data-testid="LandingPage">
					<ExternalLogin fairId={fairId} setLoggedIn={setLoggedIn} />
				</div>
			);
		}
		const schoolText = `${bookfairInfo.school} Book Fair has been closed.`;
		return (
			<div className={styles.LandingPage} data-testid="LandingPage">
				<LandingPageHeader />
				<div className={styles.body}>
					<div className={styles.closedSchool}>{schoolText}</div>
					<br></br>
					<div className={styles.salesText}>
						<div className={styles.totalText}>Total sales</div>
						<div className={styles.totalAmount}>${bookfairInfo.totalSales.toFixed(2)}</div>
					</div>
				</div>
			</div>
		);
	};

	return (
		<>
			{!loading && (
				<>
					{closedFairState === false && loadBfInfo()}
					{closedFairState === true && closedFairDesign()}
				</>
			)}
		</>
	);
}

export default LandingPage;
